"use client";

import CTA from "@/components/task/CTA";
import Footer from "@/components/task/Footer";
import Button from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowRight,
  BookHeadphones,
  ChartNoAxesCombined,
  Coins,
  Gem,
  Handshake,
  Headphones,
  Mail,
  Palette,
  Search,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

const categoriesData = {
  "All Categories": [],
  "Most Popular": {
    icon: Gem,
    roles: [],
  },
  "Creative & Production": {
    icon: Palette,
    roles: [
      {
        title: "Short-Form Editor",
        desc: "Transform clips into viral TikToks, Reels, and Shorts.",
      },
      {
        title: "Motion Graphics Artist",
        desc: "Add energy with animated titles and visual effects.",
      },
      {
        title: "Animation specialist",
        desc: "Bring ideas to life with 2D/3D animated content.",
      },
      {
        title: "Channel Branding Designer",
        desc: "Build a consistent and recognizable channel identity.",
      },
      {
        title: "Scriptwriter / Copywriter",
        desc: "Write engaging narratives that drive retention.",
      },
      {
        title: "Web Design",
        desc: "Build sleek websites that turn visitors into fans and customers.",
      },
      {
        title: "Voiceover Artist",
        desc: "Add personality and professionalism through narration.",
      },
      {
        title: "Video Editor",
        desc: "Craft seamless stories that keep viewers hooked.",
      },
      {
        title: "Podcast Editor",
        desc: "Deliver crisp, professional audio for binge-worthy podcasts.",
      },
      {
        title: "Thumbnail Designer",
        desc: "Bring ideas to life with 2D/3D animated content.",
      },
      {
        title: "Creative Director",
        desc: "Shape the overall creative vision and brand voice.",
      },
      {
        title: "Music & Sound Designer",
        desc: "Set the mood with original music and audio design.",
      },
    ],
  },
  "Growth & Strategy": {
    icon: ChartNoAxesCombined,
    roles: [
      {
        title: "Social Media Strategist",
        desc: "Expand presence with smart cross-platform tactics.",
      },
      {
        title: "Community Manager",
        desc: "Foster engagement and manage fan conversations.",
      },
      {
        title: "Engagement Specialist",
        desc: "Spark interaction through comments, polls, and shares.",
      },
      {
        title: "Content Ideas Researcher",
        desc: "Generate fresh, high-performing video concepts.",
      },
      {
        title: "YouTube Growth Strategist",
        desc: "Design strategies to maximize reach and revenue.",
      },
      {
        title: "Creator Coach / Channel Consultant",
        desc: "Guide creators with proven growth insights.",
      },
      {
        title: "SEO & Metadata Specialist",
        desc: "Optimize titles, tags, and descriptions for discovery.",
      },
      {
        title: "Market Researcher",
        desc: "Spot emerging trends before they peak.",
      },
      {
        title: "Audience Development Manager",
        desc: "Transform clips into viral TikToks, Reels, and Shorts.",
      },
    ],
  },
  "Communications & Collaboration": {
    icon: Handshake,
    roles: [
      {
        title: "Smart Inbox Manager",
        desc: "Manage outreach and fan communications.",
      },
      {
        title: "Brand Deal Negotiator",
        desc: "Secure profitable partnerships with brands.",
      },
      {
        title: "Talent Partnerships Manager",
        desc: "Build alliances with other creators and networks.",
      },
      {
        title: "PR & Outreach Specialist",
        desc: "Increase visibility with media and industry coverage.",
      },
      {
        title: "Sponsorship Coordinator",
        desc: "Match content with aligned sponsor opportunities.",
      },
      {
        title: "Event & Collab Coordinator",
        desc: "Organize creator meetups, collabs, and live events.",
      },
    ],
  },
  "Research & Support": {
    icon: BookHeadphones,
    roles: [
      {
        title: "Virtual Assistant",
        desc: "Handle admin tasks so creators can focus.",
      },
      {
        title: "Admin & Scheduling Assistant",
        desc: "Keep projects and publishing on track.",
      },
      {
        title: "Trend Scout",
        desc: "Identify growth opportunities in niche markets.",
      },
      {
        title: "Trend Analyst",
        desc: "Find new trends and get a daily suggestion for new video ideas, series implementations, and more.",
      },
    ],
  },
  "Technical & Platform Support": {
    icon: Headphones,
    roles: [
      {
        title: "Channel Upload Manager",
        desc: "Upload a set number of videos per day, slice and dice clips into compilations, dub videos onto new channels and more",
      },
      {
        title: "Copyright Protection Manager",
        desc: "Safeguard intellectual property and brand rights.",
      },
      {
        title: "Content ID Specialist",
        desc: "Protect earnings by detecting unauthorized uploads.",
      },
      {
        title: "Livestream Producer / Tech",
        desc: "Ensure smooth, professional live broadcasts.",
      },
      {
        title: "Platform Integration Specialist",
        desc: "Connect Patreon, Discord, Shopify, and tools.",
      },
      {
        title: "Data & Analytics Specialist",
        desc: "Turn performance metrics into action plans.",
      },
      {
        title: "Compliance Reviewer",
        desc: "Keep content accurate and policy-compliant.",
      },
      {
        title: "AI Tool Operator",
        desc: "Automate workflows with repurposing and captioning tools.",
      },
      {
        title: "Localization / Subtitles & Dubbing Specialist",
        desc: "Expand reach with multilingual access.",
      },
    ],
  },
  "Monetization & Business": {
    icon: Coins,
    roles: [
      {
        title: "Ad Revenue Analyst",
        desc: "Maximize income through ad performance tracking.",
      },
      {
        title: "E-commerce / Merch Manager",
        desc: "Build and manage creator merchandise lines.",
      },
      {
        title: "Creator Business Manager",
        desc: "Handle operations, planning, and partnerships.",
      },
      {
        title: "Membership Program Manager",
        desc: "Grow recurring revenue via Patreon & memberships.",
      },
      {
        title: "Accountant / Bookkeeper",
        desc: "Keep creator finances compliant and organized.",
      },
      {
        title: "Creator Finance Assistant",
        desc: "Manage invoicing, payouts, and tax preparation.",
      },
      {
        title: "Licensing & Rights Manager",
        desc: "Monetize content through licensing deals.",
      },
    ],
  },
  "Specialized & High-Value Roles": {
    icon: Gem,
    roles: [
      {
        title: "Influencer Relations Manager",
        desc: "Build long-term influencer collaborations.",
      },
      {
        title: "Crisis Manager",
        desc: "Manage PR crises and restore reputation.",
      },
      {
        title: "On-Camera Talent Assistant",
        desc: "Support talent with prep, logistics, and delivery.",
      },
    ],
  },
};

const findRoleWithIcon = (title) => {
  for (const [category, data] of Object.entries(categoriesData)) {
    const role = data.roles?.find((r) => r.title === title);
    if (role) return { ...role, icon: data.icon, category };
  }
  return null;
};

categoriesData["Most Popular"].roles = [
  findRoleWithIcon("Audience Development Manager"),
  findRoleWithIcon("Channel Upload Manager"),
  findRoleWithIcon("Trend Analyst"),
  findRoleWithIcon("Content ID Specialist"),
  findRoleWithIcon("E-commerce / Merch Manager"),
  findRoleWithIcon("Creator Finance Assistant"),
  findRoleWithIcon("Short-Form Editor"),
  findRoleWithIcon("Thumbnail Designer"),
  findRoleWithIcon("Video Editor"),
].filter(Boolean);

export default function TalentHub() {
  const { category } = useParams();
  const navigate = useNavigate();

  const categoryFromRoute = category
    ? decodeURIComponent(category)
    : "All Categories";
  const [selectedCategory, setSelectedCategory] = useState(categoryFromRoute);

  const [showAll, setShowAll] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const toggleRole = (title) => {
    setSelectedRoles((prev) =>
      prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title]
    );
  };

  const categories = Object.keys(categoriesData);

  const allRoles = Object.entries(categoriesData)
    .filter(([key]) => key !== "All Categories")
    .flatMap(([category, data]) =>
      data.roles.map((role) => ({
        ...role,
        icon: data.icon,
        category,
      }))
    );

  let rolesToShow =
    selectedCategory === "All Categories"
      ? showAll
        ? allRoles
        : allRoles.slice(0, 15)
      : categoriesData[selectedCategory].roles;

  if (searchTerm.trim() !== "") {
    rolesToShow = rolesToShow.filter(
      (role) =>
        role.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        role.desc.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [category]);

  useEffect(() => {
    setSelectedCategory(categoryFromRoute);
  }, [categoryFromRoute]);

  const selectedCountByCategory = Object.fromEntries(
    Object.entries(categoriesData).map(([cat, data]) => {
      // Handle "All Categories" being an array instead of object
      const roles = Array.isArray(data) ? data : data.roles || [];
      const count = roles.filter((role) =>
        selectedRoles.includes(role.title)
      ).length;
      return [cat, count];
    })
  );

  return (
    <div className="space-y-10 lg:space-y-20 bg-[#121212]">
      <div className="w-[90%] pt-32 mx-auto space-y-8 lg:space-y-14 4xl:max-w-[100rem]">
        <div className="space-y-4 text-center text-white">
          <h1 className="text-5xl lg:text-6xl">Explore Roles</h1>
          <p className="max-w-2xl mx-auto text-gray-300 text-sm lg:text-lg">
            From creative production to operational support, EnfiniTask connects
            you with skilled professionals who make scaling easier, faster, and
            smarter.
          </p>
        </div>
        {/* Search + Filter */}
        <div
          className={`${
            selectedRoles.length > 0 ? "flex flex-col lg:flex-row" : "flex"
          } justify-end items-center gap-4`}
        >
          <div className="relative w-full lg:w-fit">
            <Search className="absolute text-primaryLight top-1/2 -translate-y-1/2 left-4" />
            <Input
              placeholder="Search for a talent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full lg:w-60 bg-[#161616] border-[var(--primaryLight)] text-primary py-6 pl-12 rounded-full"
            />
          </div>
          <div className="flex justify-end items-center gap-4">
            {selectedRoles.length > 0 && (
              <Button
                onClick={() => setSelectedRoles([])}
                variant="outline"
                className="bg-white text-primary rounded-md py-6"
              >
                {selectedRoles.length} selected{" "}
                {selectedRoles.length === 1 ? "role" : "roles"}
                <X className="w-4 h-4 text-[#7C3AED]" />
              </Button>
            )}
            <Button
              onClick={() =>
                navigate("/hire-a-talent", { state: { selectedRoles } })
              }
              disabled={selectedRoles.length === 0}
              className="bg-primary text-white rounded-full py-6"
            >
              Build your team
              <ArrowRight />
            </Button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-10 text-white">
          {/* Sidebar */}
          <aside className="w-96 space-y-6 bg-[#161616] border border-[#434242] rounded-[20px] p-8 hidden lg:block max-h-[840px]">
            <h1 className="font-medium tracking-wide text-[#EDE9FE] text-lg">
              By Category
            </h1>
            <ul className="space-y-8 text-gray-200 pb-8">
              {categories.map((cat) => (
                <li
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`cursor-pointer transition-colors flex items-center justify-start gap-3 ${
                    selectedCategory === cat
                      ? "text-purple-400 font-medium"
                      : "hover:text-purple-400"
                  }`}
                >
                  <span>{cat}</span>
                  <span className="-ml-2">
                    {cat === "Most Popular" && <> 🔥</>}
                  </span>
                  {selectedCountByCategory[cat] > 0 &&
                    cat !== "Most Popular" && (
                      <span className="bg-primary text-white text-xs rounded-full px-2 py-1">
                        {selectedCountByCategory[cat]}
                      </span>
                    )}
                </li>
              ))}
            </ul>

            <div className="pt-6 border-t border-[#434242] space-y-4">
              <p className="text-white mb-2">Can’t find what you need?</p>
              <p className="text-[#EDE9FE] font-light text-sm">
                Tell us what you’re looking for, and we’ll help you find or
                create the right talent solution.
              </p>
              <Link to="/contact-us" className="block">
                <Button className="bg-white w-fit text-primary py-6">
                  Send us a message
                  <Mail />
                </Button>
              </Link>
            </div>
          </aside>

          {/* Select */}
          <Select onValueChange={(value) => setSelectedCategory(value)}>
            <SelectTrigger className="w-full py-6 rounded-md dark:border-[#434242] text-[#EDE9FE] lg:hidden">
              <SelectValue placeholder={selectedCategory} />
            </SelectTrigger>
            <SelectContent className="text-white p-2 bg-[#161616]">
              {categories.map((category, index) => (
                <SelectItem key={index} value={category}>
                  <div className="flex gap-3 items-center">
                    {category}
                    <span className="-ml-2">
                      {category === "Most Popular" && <> 🔥</>}
                    </span>
                    {selectedCountByCategory[category] > 0 && (
                      <span className="bg-primary text-white text-xs rounded-full px-2 py-1">
                        {selectedCountByCategory[category]}
                      </span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Content */}
          {rolesToShow.length === 0 ? (
            <p className="text-gray-400 text-center w-full">No roles found.</p>
          ) : (
            <div className="flex-1 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {rolesToShow.map((role, i) => {
                  const isSelected = selectedRoles.includes(role.title);
                  const Icon =
                    selectedCategory === "All Categories" ||
                    selectedCategory === "Most Popular"
                      ? role.icon || Palette
                      : categoriesData[selectedCategory].icon || Palette;

                  return (
                    <Card
                      key={i}
                      onClick={() => toggleRole(role.title)}
                      className={`relative cursor-pointer transition rounded-[20px] ${
                        isSelected
                          ? "bg-white text-black border-purple-500"
                          : "bg-[#161616] text-white border border-[#2a2a2a] hover:border-purple-500"
                      }`}
                    >
                      {/* Checkbox */}
                      <Checkbox
                        type="checkbox"
                        checked={isSelected}
                        onClick={(e) => e.stopPropagation()}
                        onCheckedChange={() => toggleRole(role.title)}
                        className="absolute top-6 right-5 w-5 h-5 cursor-pointer"
                      />
                      {categoriesData["Most Popular"].roles.some(
                        (r) => r.title === role.title
                      ) && (
                        <div className="rounded-full text-sm w-fit px-4 py-2 full border border-[#434242] absolute top-4 right-14">
                          🔥 Hot!
                        </div>
                      )}

                      <CardHeader className="flex justify-between">
                        <CardTitle
                          className={`p-4 rounded-full w-fit ${
                            isSelected
                              ? "bg-purple-100 text-purple-600"
                              : "bg-[#7C3AED]/20 text-primary"
                          }`}
                        >
                          <Icon className="h-8 w-8" />
                        </CardTitle>
                      </CardHeader>

                      <CardContent className="space-y-2">
                        <h1
                          className={`text-xl ${
                            isSelected
                              ? "text-primary font-medium"
                              : "text-primaryLight"
                          }`}
                        >
                          {role.title}
                        </h1>
                        <p
                          className={
                            isSelected ? "text-gray-700" : "text-[#EDE9FE]"
                          }
                        >
                          {role.desc}
                        </p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              {selectedCategory === "All Categories" &&
                !showAll &&
                !searchTerm && (
                  <div className="flex justify-center mt-6">
                    <Button
                      className="bg-transparent border border-[#434242] text-white px-8 py-6 rounded-full"
                      onClick={() => setShowAll(true)}
                    >
                      Show All
                    </Button>
                  </div>
                )}
            </div>
          )}
        </div>
      </div>

      <div className="min-h-[900px] bg-[#1a1a1a] lg:pb-36 xxs:pb-60">
        <CTA />
      </div>
      <Footer />
    </div>
  );
}
